import { uniqBy } from 'lodash'
import { ChannelAllowExportEnum } from '@c2b/muven-commons'
import { ChannelModel } from '../../channel/models/ChannelModel'
import { ChannelSettingsFormModel } from '../../channel/models/ChannelSettingsFormModel'
import { ProductModel } from './ProductModel'
import { ProductStoreModel } from './ProductStoreModel'
import { ProductStoreChannelModel } from './ProductStoreChannelModel'

export enum IntegrationIssueOriginEnum {
    Retaguarda = 'RETAGUARDA',
    Muven = 'MUVEN',
    Canal = 'CANAL',
}

export enum IntegrationIssueEnum {
    NoStores = 'NO_STORES',
    StoresWithoutChannels = 'STORES_WITHOUT_CHANNELS',
    NotAssociatedInChannel = 'NOT_ASSOCIATED_IN_CHANNEL',
    ChannelShouldHavePublished = 'CHANNEL_SHOULD_HAVE_PUBLISHED',
    ChannelsFilteredByPriceStore = 'CHANNELS_FILTERED_BY_PRICE_STORE',
    AllStoresInactive = 'ALL_STORES_INACTIVE',
    AllProductStoresInactive = 'ALL_PRODUCT_STORES_INACTIVE',
    VariationsWithoutStores = 'VARIATIONS_WITHOUT_STORES',
    ParentWithoutStores = 'PARENT_WITHOUT_STORES',
}

/**
 * Elos da cadeia de integração, na ordem em que a informação percorre até
 * chegar ao canal. O primeiro elo rompido é o que explica o aviso na tela.
 */
export enum IntegrationChainStepEnum {
    Retaguarda = 'RETAGUARDA',
    ProductStore = 'PRODUCT_STORE',
    ProductStoreChannel = 'PRODUCT_STORE_CHANNEL',
    Canal = 'CANAL',
}

export interface IntegrationChainStep {
    step: IntegrationChainStepEnum;
    label: string;
    hint: string;
    isBroken: boolean;
    isReached: boolean;
}

export class IntegrationIssue {
    code: IntegrationIssueEnum;
    origin: IntegrationIssueOriginEnum;
    isBlocking: boolean;
    title: string;
    detail: string;
    whoFixes: string;
    affected: string[];

    constructor(i: Partial<IntegrationIssue>) {
        this.code = i.code as IntegrationIssueEnum
        this.origin = i.origin as IntegrationIssueOriginEnum
        this.isBlocking = !!i.isBlocking
        this.title = i.title || ''
        this.detail = i.detail || ''
        this.whoFixes = i.whoFixes || ''
        this.affected = i.affected || []
    }
}

export interface DiagnosticChannelRow {
    name: string;
    idChannel?: string;
    idExternalChannel?: string;
    hasExternalChannelId: boolean;
    isActive: boolean;
    validInChannel: boolean;
    filteredByPriceStore: boolean;
    priceManagementStoreId?: number;
    /**
     * undefined quando o canal não foi encontrado na lista de canais e, portanto,
     * não dá para afirmar nada sobre a configuração de exportação dele.
     */
    exportsNewProducts?: boolean;
    allowExportDescription?: string;
}

export interface DiagnosticActionLink {
    label: string;
    route: string;
}

export interface DiagnosticAction {
    title: string;
    description: string;
    /** Rotas internas para onde a ação leva. Abrem sempre em nova aba. */
    links?: DiagnosticActionLink[];
    /** Ação que o próprio painel executa, em vez de só orientar o usuário. */
    command?: 'force-sync';
    tone: 'action'|'warning';
}

interface ProductStoreSource {
    productStore: ProductStoreModel;
    variationName: string;
    isFromVariation: boolean;
}

export interface DiagnosticStoreRow {
    storeName: string;
    idStore?: string;
    idProductStore?: string;
    storeIsActive: boolean;
    productStoreIsActive: boolean;
    variationName: string;
    isFromVariation: boolean;
    channels: DiagnosticChannelRow[];
}

const WHO_FIXES_BACKEND = 'Suporte da sua retaguarda (ERP)'
const WHO_FIXES_MUVEN_CHANNEL_CONFIG = 'Configuração do canal no Muven'
const WHO_FIXES_MUVEN_ASSOCIATION = 'Associação de produtos, aqui no Muven'
/**
 * Quando o canal exporta novos produtos, quem cria o vínculo é a automação do
 * Muven, não uma pessoa. Se ela não concluir, o usuário tenta as sugestões e,
 * persistindo, aciona o suporte da retaguarda, que é o canal de atendimento
 * dele. Não existe "suporte Muven" para o cliente.
 */
const WHO_FIXES_MUVEN_AUTOMATIC = 'Em geral, o próprio Muven automaticamente. Se não concluir, siga as sugestões a seguir.'

/** Configurações em que o Muven cria o anúncio no canal por conta própria. */
const ALLOW_EXPORT_THAT_PUBLISHES = [
    ChannelAllowExportEnum.All,
    ChannelAllowExportEnum.InsertProductAndUpdateStockAndPrice,
    ChannelAllowExportEnum.InsertProductAndUpdateStock,
]

export class ProductIntegrationDiagnosticModel {
    /**
     * Mesma condição aplicada por ProductModel.getUniqueStoreChannels(): um canal
     * com loja de gestão de preço definida só é considerado na loja correspondente.
     */
    static isChannelFilteredByPriceStore(channel: ProductStoreChannelModel, productStore: ProductStoreModel): boolean {
        const idPriceManagementStore = channel.channel?.idPriceManagementStore

        if (idPriceManagementStore === undefined || idPriceManagementStore === null) {
            return false
        }

        return Number(idPriceManagementStore) !== Number(productStore.idStore)
    }

    /** Configurações em que o Muven publica o produto no canal sem intervenção do usuário. */
    static publishesNewProducts(channel: ChannelModel): boolean {
        return ALLOW_EXPORT_THAT_PUBLISHES.indexOf(channel.allowExport) !== -1
    }

    static getAllowExportDescription(channel: ChannelModel): string {
        const option = ChannelSettingsFormModel.getAllowExportOptions().find((o) => {
            return o.enum === channel.allowExport
        })

        return option ? option.description : ''
    }

    private _product: ProductModel;
    private _backendName: string;
    private _channelList: ChannelModel[];
    private _isBackendShop: boolean;
    private _productAuditUrl: string;
    private _issues: IntegrationIssue[] = [];
    private _storeRows: DiagnosticStoreRow[] = [];

    constructor(
        product?: ProductModel,
        backendName: string = 'da sua retaguarda',
        channelList: ChannelModel[] = [],
        isBackendShop: boolean = false,
        productAuditUrl: string = ''
    ) {
        this._product = product || new ProductModel()
        this._backendName = backendName || 'da sua retaguarda'
        this._channelList = channelList || []
        this._isBackendShop = isBackendShop
        this._productAuditUrl = productAuditUrl || ''

        this._buildStoreRows()
        this._buildIssues()
    }

    hasIssues(): boolean {
        return !!this._issues.length
    }

    getIssues(): IntegrationIssue[] {
        return this._issues
    }

    getBlockingIssues(): IntegrationIssue[] {
        return this._issues.filter((i) => i.isBlocking)
    }

    getWarningIssues(): IntegrationIssue[] {
        return this._issues.filter((i) => !i.isBlocking)
    }

    getStoreChannelMatrix(): DiagnosticStoreRow[] {
        return this._storeRows
    }

    hasStoreChannelMatrix(): boolean {
        return !!this._storeRows.length
    }

    getBackendName(): string {
        return this._backendName
    }

    /** O produto tem variações em grade (o caminho de canais é outro). */
    hasVariations(): boolean {
        return !!this._product.children.filter((child) => child.hasAttributeTypeGrid()).length
    }

    /**
     * Réplica de ProductFormModel._parseChannels(): só entram canais das lojas do
     * produto-pai que já tenham idExternalChannel. É esta lista que alimenta
     * productForm.channels e, portanto, o aviso na tela de edição.
     */
    getFormChannelsCount(): number {
        const withExternalId = this._product.getStoreChannelsWithIdExternalChannel()

        if (this.hasVariations()) {
            return withExternalId.length
        }

        // Sem variações, ProductFormModel.setChannels() recupera os demais canais
        // via getUniqueStoreChannels(), que não exige idExternalChannel.
        const recovered = this._product.getUniqueStoreChannels()

        return uniqBy(withExternalId.concat(recovered), 'channel.id').length
    }

    /**
     * Canais que sobrevivem ao filtro de loja de gestão de preço. É o que
     * ProductFormCmp.checkIfCanSubmit() usa para liberar o salvamento.
     */
    getUsableChannelsCount(): number {
        return this._storeRows.reduce((total, row) => {
            return total + row.channels.filter((channel) => !channel.filteredByPriceStore).length
        }, 0)
    }

    /** O aviso da tela de edição aparece quando não sobra nenhum canal no formulário. */
    isEditFormBlocked(): boolean {
        return this.getFormChannelsCount() === 0
    }

    /**
     * Caso particular: com variações, o formulário mostra o aviso mesmo havendo
     * canais utilizáveis, porque setChannels() só preenche variationsChannels.
     * O salvamento, que reagrupa essa lista, continua funcionando.
     */
    isFalsePositiveOnVariations(): boolean {
        return this.isEditFormBlocked() && this.hasVariations() && this.getUsableChannelsCount() > 0
    }

    /**
     * O canal exporta novos produtos, então o próprio Muven deveria resolver.
     * Vale tanto para o produto que já tem linha de canal sem anúncio quanto
     * para o que não tem linha nenhuma, por isso olha o responsável e não o
     * código da causa.
     */
    shouldMuvenResolveAutomatically(): boolean {
        return this._issues.some((issue) => issue.whoFixes === WHO_FIXES_MUVEN_AUTOMATIC)
    }

    /**
     * Frase que responde "por que este aviso apareceu?", a partir do que foi
     * de fato observado no produto - e não de uma suposição sobre a retaguarda.
     */
    getTriggerExplanation(): string {
        if (this.isParentWithoutStoresBlocking()) {
            return `As variações chegaram do ${this._backendName} com empresa e canal, mas o produto pai não ` +
                `recebeu vínculo próprio com nenhuma empresa. O canal Tray exige esse vínculo para enviar o ` +
                `produto pai, por isso nada deste produto sobe para a Tray. Os demais canais não são ` +
                `bloqueados por isso.`
        }

        if (!this._storeRows.length) {
            return `O Muven não recebeu do ${this._backendName} nenhum vínculo entre este produto e uma empresa. ` +
                `Sem esse vínculo não há preço, estoque nem canal e, por isso, não há como enviar para os canais.`
        }

        const allChannels = this._getAllChannels()

        // Aqui fica só o porquê. O que a configuração dos canais indica e o que
        // fazer a respeito são papel do card da causa e das sugestões.
        if (!allChannels.length) {
            return `As empresas chegaram do ${this._backendName}, mas o produto não foi vinculado a nenhum canal. ` +
                `Esse vínculo é criado pelo Muven, na exportação automática ou na associação manual, ` +
                `e não depende da retaguarda.`
        }

        if (!this._getChannelsWithExternalId().length) {
            const autoPublishing = allChannels.filter((channel) => channel.exportsNewProducts === true)
            const manualOnly = allChannels.filter((channel) => channel.exportsNewProducts === false)

            let base = `As empresas e os canais chegaram corretamente. O que falta é a associação do produto ` +
                `ao anúncio no canal: nenhum canal tem o identificador do anúncio preenchido. ` +
                `Essa associação é responsabilidade do Muven, não vem ${this._backendName}.`

            if (autoPublishing.length && manualOnly.length) {
                base = `As empresas e os canais chegaram corretamente, mas nenhum canal tem o identificador ` +
                    `do anúncio. E os canais deste produto estão em situações diferentes: parte está ` +
                    `configurada para exportar novos produtos e deveria ter publicado sozinha, enquanto a ` +
                    `outra parte nunca cria anúncio por conta própria e depende de associação manual. ` +
                    `A lista de causas abaixo separa quais canais são quais.`
            } else if (autoPublishing.length) {
                base = `As empresas e os canais chegaram corretamente, e o canal está configurado para ` +
                    `exportar novos produtos automaticamente. Mesmo assim o vínculo com o canal foi criado ` +
                    `sem o identificador do anúncio. Ou seja: o Muven deveria ter publicado este produto ` +
                    `sozinho e não publicou, ou o produto foi desassociado depois. A auditoria do produto ` +
                    `mostra qual dos dois aconteceu.`
            } else if (manualOnly.length) {
                base = `As empresas e os canais chegaram corretamente, mas nenhum canal tem o identificador ` +
                    `do anúncio. A configuração destes canais não cria anúncios novos, então o Muven nunca ` +
                    `publicaria este produto sozinho: a associação precisa ser feita manualmente. ` +
                    `Isso não depende ${this._backendName}.`
            }

            if (this.isFalsePositiveOnVariations()) {
                return `${base} Como este produto tem variações, o formulário exibe o aviso mesmo assim, ` +
                    `mas o salvamento continua funcionando - as alterações são aplicadas às variações.`
            }

            return base
        }

        if (!this.getUsableChannelsCount()) {
            return `As empresas e os canais chegaram, mas todos os canais estão configurados no Muven para ` +
                `gerenciar preço por uma empresa diferente da que está vinculada a este produto. ` +
                `Por isso o Muven os ignora aqui. É configuração do canal, não da retaguarda.`
        }

        return 'Não foi identificada nenhuma falha de integração neste produto.'
    }

    /**
     * Resumo de uma linha para o alerta que barra o salvamento. Nomeia o
     * responsável em vez de mandar todo caso para o suporte da retaguarda:
     * quando falta publicar ou associar no canal, quem resolve é o Muven ou o
     * próprio usuário.
     */
    getBlockedSaveMessage(): string {
        const issue = this.getBlockingIssues()[0]

        if (!issue) {
            return 'Não é possível salvar: este produto não está vinculado a nenhum canal. ' +
                'Veja o diagnóstico de integração no topo da página.'
        }

        // whoFixes pode ou não terminar em ponto, conforme a causa.
        const whoFixes = issue.whoFixes.replace(/\.+$/, '')

        return `Não é possível salvar: ${this._lowerFirst(issue.title)}. ` +
            `Quem resolve: ${whoFixes}. Abra "Ver mais detalhes" no topo da página.`
    }

    /*
     * PAI DE GRADE SEM PRODUCT_STORE: DESATIVADO em 07/10/2026.
     *
     * O agente da Tray não envia o produto pai de grade que não tem product_store
     * próprio (caso do produto 25435). Esta detecção explicava isso no painel, mas
     * dependia de ProductModel.storesCopiedFromChild, que normalizeV1 marcava
     * também em todo produto simples Bimer. O diagnóstico então descartava as
     * stores do produto e acusava "não vinculado a nenhuma empresa" em quase todos.
     *
     * Para religar:
     *   1. em normalizeV1, marcar storesCopiedFromChild só quando houver grade;
     *   2. descomentar o corpo de isParentWithoutOwnStores e _getParentOwnStores;
     *   3. descomentar o push em _buildIssues e as linhas em toSupportText;
     *   4. tirar o .skip do bloco "causa 8" nos testes.
     */

    /**
     * Produto em grade cujo pai não tem product_store próprio, só as variações.
     * A matriz junta as linhas do pai e dos filhos, então sem esta verificação
     * o caso passa despercebido. Só conta enquanto falta associar algum canal:
     * nos canais que trabalham por SKU (Mercado Livre, Magento) o pai nunca é
     * enviado e, portanto, nunca ganha vínculo, sem que isso seja problema.
     */
    isParentWithoutOwnStores(): boolean {
        // Desativado, ver o bloco "PAI DE GRADE SEM PRODUCT_STORE" acima.
        return false

        // const hasPendingChannel = !this._getAllChannels().length || !!this._getUnassociatedChannels().length
        //
        // return this.hasVariations() &&
        //     !this._getParentOwnStores().length &&
        //     this._storeRows.some((row) => row.isFromVariation) &&
        //     hasPendingChannel
    }

    /**
     * Canais Tray envolvidos no produto. O agente da Tray só envia o produto pai
     * quando ele próprio está ativo no canal, o que exige product_store do pai.
     * Os demais agentes não têm essa trava (Tray Fbits inclusive). Canal Tray
     * já associado não entra: se o anúncio existe, o pai já foi enviado.
     */
    getTrayChannelsAffected(): string[] {
        const fromRows = this._storeRows.reduce((acc, row) => {
            row.channels.filter((channel) => !channel.hasExternalChannelId).forEach((channel) => {
                const configured = channel.idChannel
                    ? this._channelList.find((c) => String(c.id) === String(channel.idChannel))
                    : undefined

                if (configured && configured.isTray()) {
                    acc.push(configured.name || channel.name)
                }
            })

            return acc
        }, [] as string[])

        if (this._getAllChannels().length) {
            return Array.from(new Set(fromRows))
        }

        return this._channelList.filter((c) => c.isTray()).map((c) => c.name)
    }

    /** Na Tray o pai sem vínculo próprio nunca é enviado: é bloqueante. */
    isParentWithoutStoresBlocking(): boolean {
        return this.isParentWithoutOwnStores() && !!this.getTrayChannelsAffected().length
    }

    /**
     * O primeiro elo da cadeia que não foi alcançado. É o que o painel destaca.
     */
    getChain(): IntegrationChainStep[] {
        const parentBlocked = this.isParentWithoutStoresBlocking()
        const hasStores = !!this._storeRows.length && !parentBlocked
        const hasChannels = !!this._getAllChannels().length
        const hasUsableChannels = this.getUsableChannelsCount() > 0
        const hasExternalId = !!this._getChannelsWithExternalId().length

        return [
            {
                step: IntegrationChainStepEnum.Retaguarda,
                label: 'Retaguarda',
                hint: 'Envia o produto e o vínculo com a empresa',
                isReached: true,
                isBroken: false,
            },
            {
                step: IntegrationChainStepEnum.ProductStore,
                label: 'Produto na empresa',
                hint: parentBlocked ? 'Produto pai sem vínculo com a empresa' : 'Vínculo criado pela retaguarda',
                isReached: hasStores,
                isBroken: !hasStores,
            },
            {
                step: IntegrationChainStepEnum.ProductStoreChannel,
                label: 'Produto no canal',
                hint: 'Canal de venda habilitado para o produto na empresa',
                isReached: hasStores && hasChannels && hasUsableChannels,
                isBroken: hasStores && (!hasChannels || !hasUsableChannels),
            },
            {
                step: IntegrationChainStepEnum.Canal,
                label: 'Anúncio associado',
                hint: 'Produto ligado ao anúncio correspondente no canal',
                isReached: hasStores && hasChannels && hasUsableChannels && hasExternalId,
                isBroken: hasStores && hasChannels && hasUsableChannels && !hasExternalId,
            },
        ]
    }

    getBrokenStep(): IntegrationChainStep|undefined {
        return this.getChain().find((s) => s.isBroken)
    }

    /**
     * O que o próprio usuário pode tentar antes de abrir chamado. Só faz sentido
     * quando o que falta é a associação com o anúncio: se o vínculo com a empresa
     * nem chegou, forçar sincronização de canal não resolve nada.
     */
    getSuggestedActions(): DiagnosticAction[] {
        const broken = this.getBrokenStep()
        const isChannelSide = !!broken && (
            broken.step === IntegrationChainStepEnum.Canal ||
            broken.step === IntegrationChainStepEnum.ProductStoreChannel
        )

        if (!isChannelSide) {
            return this._getGridExportTip()
        }

        const actions: DiagnosticAction[] = []

        // Primeiro passo sempre: a auditoria diz se o Muven tentou enviar e o que
        // o canal respondeu. Os motivos de recusa são variados demais para listar
        // aqui, e boa parte deles o usuário resolve sem abrir chamado.
        actions.push({
            title: 'Analise a auditoria',
            description: 'A auditoria mostra se o Muven tentou enviar este produto e qual foi a resposta ' +
                'do canal. É ali que aparece o motivo da recusa, e boa parte deles você mesmo resolve. ' +
                'Se não encontrar nada na auditoria deste produto, procure na auditoria geral: ' +
                'alguns registros só aparecem lá.',
            links: this._getAuditLinks(),
            tone: 'action',
        })

        const unassociated = this._getUnassociatedChannels()
        const publishing = unassociated.filter((channel) => channel.exportsNewProducts !== false)
        const manualOnly = unassociated.filter((channel) => channel.exportsNewProducts === false)

        // Sem conhecer canal nenhum não dá para afirmar o que a configuração faz
        // nem para onde levar o usuário. A auditoria acima já basta.
        if (!unassociated.length && !this._channelList.length) {
            return actions.concat(this._getGridExportTip())
        }

        // Quando o produto não tem nenhuma linha de canal, os links saem dos
        // canais configurados no assinante.
        const configuredPublishing = this._configuredChannelsThatPublish()
        const canBeCreatedBySync = unassociated.length ? !!publishing.length : !!configuredPublishing.length

        // Forçar sincronização só cria o anúncio em canal que exporta produto novo.
        // Num canal de só estoque/preço isso nunca funcionaria, então nem sugerimos.
        if (canBeCreatedBySync) {
            const idChannel = unassociated.length
                ? this._firstChannelId(publishing)
                : this._firstConfiguredChannelId(configuredPublishing)

            actions.push({
                title: 'Force a sincronização e acompanhe a fila',
                description: 'Peça ao Muven para exportar este produto novamente para os canais e aguarde o ' +
                    'término. O andamento aparece na fila de processamento do canal.',
                links: idChannel
                    ? [{
                        label: 'Abrir fila de processamento',
                        route: `/canais/${idChannel}/configuracoes/informacoes/fila-processamento`,
                    }]
                    : undefined,
                command: 'force-sync',
                tone: 'action',
            })
        }

        const idAssociationChannel = unassociated.length
            ? this._firstChannelId(manualOnly.length ? manualOnly : unassociated)
            : this._firstConfiguredChannelId(this._channelList)

        actions.push({
            title: canBeCreatedBySync
                ? 'Se o produto já existe no canal, faça o vínculo manual'
                : 'Faça o vínculo manual com o anúncio',
            description: canBeCreatedBySync
                ? 'Quando o anúncio já foi criado no canal, basta associá-lo ao produto do Muven. ' +
                    'Faça a busca de acordo com as intruções do canal'
                : 'A configuração destes canais não cria anúncios novos, então a associação manual é o ' +
                    'caminho para este produto. Crie o anúncio no canal, se ainda não existir, e associe-o ' +
                    'ao produto do Muven.',
            links: idAssociationChannel
                ? [{
                    label: 'Abrir associação de produtos',
                    route: `/canais/${idAssociationChannel}/configuracoes/associacoes/produtos`,
                }]
                : undefined,
            tone: 'action',
        })

        actions.push({
            title: 'Nunca exclua o produto direto no canal',
            description: 'Excluir o anúncio pelo painel do canal rompe o vínculo com o Muven e passa a gerar ' +
                'outros erros de sincronização.',
            tone: 'warning',
        })

        return actions.concat(this._getGridExportTip())
    }

    toSupportText(): string {
        const lines: string[] = []
        const p = this._product

        lines.push('DIAGNOSTICO DE INTEGRACAO - Muven')
        lines.push(`Produto: #${p.id || '-'} | SKU: ${p.skuCode || '-'} | EAN: ${p.eanCode || '-'}`)
        lines.push(`Nome: ${p.name || '-'}`)
        lines.push(`Retaguarda: ${this._backendName}`)
        lines.push(`Produto com variacoes: ${this.hasVariations() ? 'sim' : 'nao'}`)

        // Desativado, ver o bloco "PAI DE GRADE SEM PRODUCT_STORE".
        // if (this.hasVariations()) {
        //     lines.push(`Produto pai com product_store proprio: ${this._getParentOwnStores().length ? 'sim' : 'nao'}`)
        // }
        //
        // if (this.isParentWithoutStoresBlocking()) {
        //     lines.push('ATENCAO: canal Tray exige product_store do produto pai para o primeiro envio.')
        // }

        const broken = this.getBrokenStep()
        lines.push(`Elo rompido: ${broken ? broken.label : 'nenhum'}`)
        lines.push(`Canais no formulario: ${this.getFormChannelsCount()}` +
            ` | canais utilizaveis: ${this.getUsableChannelsCount()}`)

        if (this.isFalsePositiveOnVariations()) {
            lines.push('ATENCAO: aviso exibido por limitacao do formulario (produto com variacoes);' +
                ' o salvamento continua funcionando.')
        }

        lines.push('')
        lines.push(`Causas detectadas (${this._issues.length}):`)

        if (!this._issues.length) {
            lines.push('  nenhuma')
        }

        this._issues.forEach((issue, index) => {
            lines.push(`  [${index + 1}] ${issue.title}${issue.isBlocking ? ' (BLOQUEANTE)' : ''}`)
            lines.push(`      codigo: ${issue.code} | origem: ${issue.origin}`)
            lines.push(`      ${issue.detail}`)
            lines.push(`      quem resolve: ${issue.whoFixes}`)

            if (issue.affected.length) {
                lines.push(`      afetados: ${issue.affected.join('; ')}`)
            }
        })

        lines.push('')
        lines.push(`Tabela product_store (${this._storeRows.length} registros):`)

        if (!this._storeRows.length) {
            lines.push('  nenhum registro product_store')
        }

        this._storeRows.forEach((store, index) => {
            const variation = store.variationName ? ` | variacao: ${store.variationName}` : ''

            lines.push(
                `  [${index + 1}] ${store.storeName} (loja ${store.storeIsActive ? 'ativa' : 'INATIVA'})` +
                ` | product_store id=${store.idProductStore || '-'} idStore=${store.idStore || '-'}` +
                ` ${store.productStoreIsActive ? 'ativo' : 'INATIVO'}${variation}` +
                ` | canais: ${store.channels.length}`
            )

            store.channels.forEach((channel) => {
                lines.push(
                    `      - ${channel.name} | idExternalChannel: ${channel.idExternalChannel || 'AUSENTE'}` +
                    ` | ativo: ${channel.isActive ? 'sim' : 'nao'}` +
                    ` | valido no canal: ${channel.validInChannel ? 'sim' : 'nao'}` +
                    (channel.allowExportDescription ? ` | exportacao: ${channel.allowExportDescription}` : '') +
                    (channel.exportsNewProducts === true && !channel.hasExternalChannelId
                        ? ' | ATENCAO: canal publica novos produtos, mas o anuncio nao foi associado'
                        : '') +
                    (channel.filteredByPriceStore
                        ? ` | DESCARTADO: canal gerencia preco pela loja ${channel.priceManagementStoreId}`
                        : '')
                )
            })
        })

        const actions = this.getSuggestedActions()

        if (actions.length) {
            lines.push('')
            lines.push('O que ja foi orientado ao usuario:')

            actions.forEach((action, index) => {
                lines.push(`  [${index + 1}] ${action.title}`)
                lines.push(`      ${action.description}`)

                ;(action.links || []).forEach((link) => {
                    lines.push(`      ${link.label}: ${link.route}`)
                })
            })
        }

        lines.push('')
        lines.push(`Gerado em ${new Date().toLocaleString('pt-BR')}`)

        return lines.join('\n')
    }

    /**
     * A configuração de exportação confiável vem da lista de canais carregada pela
     * tela, e não do canal embutido no produto, que pode chegar incompleto.
     */
    private _findConfiguredChannel(productStoreChannel: ProductStoreChannelModel): ChannelModel|undefined {
        const id = productStoreChannel.idChannel || productStoreChannel.channel?.id

        if (!id) {
            return undefined
        }

        return this._channelList.find((channel) => String(channel.id) === String(id))
    }

    /**
     * Dica preventiva para grade exportada pela metade. Quando a grade ainda está
     * sendo montada na retaguarda, as variações chegam aos poucos e o produto pai
     * pode não ser reprocessado, ficando parado. Vale para qualquer elo rompido,
     * porque a falha pode aparecer tanto no vínculo com a empresa quanto no canal.
     */
    private _getGridExportTip(): DiagnosticAction[] {
        if (!this._isBackendShop || !this.hasVariations() || !this.hasIssues()) {
            return []
        }

        return [{
            title: 'Produto em grade: monte a grade inteira antes de exportar',
            description: 'Se o produto for exportado para o Muven com a grade ainda incompleta, as variações ' +
                'chegam aos poucos e o produto pai pode não ser reprocessado, ficando sem subir para os ' +
                'canais. Na retaguarda, crie e configure toda a grade primeiro e só depois exporte o ' +
                'produto para o Muven.',
            tone: 'warning',
        }]
    }

    private _getUnassociatedChannels(): DiagnosticChannelRow[] {
        return this._getAllChannels().filter((channel) => !channel.hasExternalChannelId)
    }

    /** O canal que um link de resolução deve abrir, dentro do grupo já filtrado. */
    private _firstChannelId(channels: DiagnosticChannelRow[]): string|undefined {
        const channel = channels.find((c) => !!c.idChannel)

        return channel ? channel.idChannel : undefined
    }

    /**
     * Sem nenhuma linha de canal no produto, não há canal para consultar: a
     * decisão sai da configuração dos canais do assinante.
     */
    private _configuredChannelsThatPublish(): ChannelModel[] {
        return this._channelList.filter((channel) => {
            return ProductIntegrationDiagnosticModel.publishesNewProducts(channel)
        })
    }

    /**
     * A auditoria do produto nem sempre registra a recusa: parte dos erros só
     * aparece na auditoria geral. Por isso os dois caminhos são oferecidos.
     */
    private _getAuditLinks(): DiagnosticActionLink[] {
        const links: DiagnosticActionLink[] = []

        if (this._productAuditUrl) {
            links.push({label: 'Auditoria deste produto', route: this._productAuditUrl})
        }

        links.push({label: 'Auditoria geral', route: '/auditorias'})

        return links
    }

    private _firstConfiguredChannelId(channels: ChannelModel[]): string|undefined {
        const channel = channels.find((c) => !!c.id)

        return channel ? String(channel.id) : undefined
    }

    private _whoFixesMissingChannelLink(): string {
        if (!this._channelList.length) {
            return WHO_FIXES_MUVEN_ASSOCIATION
        }

        return this._configuredChannelsThatPublish().length
            ? WHO_FIXES_MUVEN_AUTOMATIC
            : WHO_FIXES_MUVEN_ASSOCIATION
    }

    private _describeWhoCreatesChannelLink(): string {
        if (!this._channelList.length) {
            return 'Confira em "Canais → Configurações" se o canal exporta novos produtos; caso não ' +
                'exporte, a associação é manual.'
        }

        const publishing = this._configuredChannelsThatPublish()

        if (!publishing.length) {
            return 'Nenhum canal configurado exporta novos produtos, então o Muven não cria o vínculo ' +
                'sozinho: use "Canais → Associação de produtos".'
        }

        const names = publishing.map((channel) => channel.name).join(', ')

        return `Há canal configurado para exportar novos produtos (${names}), então o Muven deveria criar ` +
            'esse vínculo por conta própria.'
    }

    /** Os títulos das causas começam em maiúscula e aqui entram no meio da frase. */
    private _lowerFirst(text: string): string {
        if (!text) {
            return ''
        }

        return text.charAt(0).toLowerCase() + text.slice(1)
    }

    private _getAllChannels(): DiagnosticChannelRow[] {
        return this._storeRows.reduce((acc, row) => acc.concat(row.channels), [] as DiagnosticChannelRow[])
    }

    private _getChannelsWithExternalId(): DiagnosticChannelRow[] {
        return this._getAllChannels().filter((channel) => channel.hasExternalChannelId)
    }

    /**
     * As stores que o pai tem de fato. Na Bimer, normalizeV1 preenche o pai com
     * as stores da primeira variação: essas linhas não são do pai e, se
     * entrassem aqui, apareceriam duplicadas e esconderiam a falta do vínculo.
     */
    private _getParentOwnStores(): ProductStoreModel[] {
        // Desativado, ver o bloco "PAI DE GRADE SEM PRODUCT_STORE".
        // return this._product.storesCopiedFromChild ? [] : this._product.stores
        return this._product.stores
    }

    private _getProductStoreSources(): ProductStoreSource[] {
        const sources: ProductStoreSource[] = []

        this._getParentOwnStores().forEach((productStore) => {
            sources.push({productStore, variationName: '', isFromVariation: false})
        })

        this._product.children.forEach((child) => {
            const variationName = child.getVariationsStringified() || child.skuCode || `#${child.id}`

            ;(child.stores || []).forEach((productStore) => {
                sources.push({productStore, variationName, isFromVariation: true})
            })
        })

        return sources
    }

    private _buildStoreRows(): void {
        this._storeRows = this._getProductStoreSources().map(({productStore, variationName, isFromVariation}) => {
            const store = productStore.store

            let storeName = '(empresa não informada)'

            if (store) {
                storeName = `${store.idSource || '-'} - ${store.name || '(sem nome)'}`
            }

            return {
                storeName,
                idStore: productStore.idStore,
                idProductStore: productStore.id,
                storeIsActive: store ? store.isActive : false,
                productStoreIsActive: productStore.isActive,
                variationName,
                isFromVariation,
                channels: (productStore.channels || []).map((channel) => {
                    const configured = this._findConfiguredChannel(channel)

                    return {
                        name: channel.channel?.name || '(canal sem nome)',
                        idChannel: channel.idChannel || channel.channel?.id,
                        idExternalChannel: channel.idExternalChannel,
                        hasExternalChannelId: !!channel.idExternalChannel,
                        isActive: channel.isActive,
                        validInChannel: channel.validInChannel,
                        filteredByPriceStore: ProductIntegrationDiagnosticModel
                            .isChannelFilteredByPriceStore(channel, productStore),
                        priceManagementStoreId: channel.channel?.idPriceManagementStore,
                        exportsNewProducts: configured
                            ? ProductIntegrationDiagnosticModel.publishesNewProducts(configured)
                            : undefined,
                        allowExportDescription: configured
                            ? ProductIntegrationDiagnosticModel.getAllowExportDescription(configured)
                            : undefined,
                    }
                }),
            }
        })
    }

    private _buildIssues(): void {
        const issues: IntegrationIssue[] = []
        const rows = this._storeRows
        const allChannels = this._getAllChannels()

        if (!rows.length) {
            issues.push(new IntegrationIssue({
                code: IntegrationIssueEnum.NoStores,
                origin: IntegrationIssueOriginEnum.Retaguarda,
                isBlocking: true,
                title: 'O produto não está vinculado a nenhuma empresa',
                detail: `O Muven não recebeu do ${this._backendName} nenhum vínculo entre este produto e uma ` +
                    `empresa. Esse vínculo é sempre criado pela retaguarda - o Muven nunca o cria. ` +
                    `Sem ele não há preço, estoque nem canal para o produto.`,
                whoFixes: WHO_FIXES_BACKEND,
            }))

            this._issues = issues
            return
        }

        // Desativado, ver o bloco "PAI DE GRADE SEM PRODUCT_STORE".
        // if (this.isParentWithoutOwnStores()) {
        //     issues.push(this._buildParentWithoutStoresIssue())
        // }

        const storesWithoutChannels = rows.filter((row) => !row.channels.length)

        // O vínculo do produto com o canal é criado pelo Muven, na exportação
        // automática ou na associação manual. A retaguarda não participa disso.
        if (storesWithoutChannels.length === rows.length) {
            issues.push(new IntegrationIssue({
                code: IntegrationIssueEnum.StoresWithoutChannels,
                origin: IntegrationIssueOriginEnum.Muven,
                isBlocking: true,
                title: 'As empresas existem, mas o produto não foi vinculado a nenhum canal',
                detail: `O produto não foi habilitado em nenhum canal de venda. ` +
                    `${this._describeWhoCreatesChannelLink()}`,
                whoFixes: this._whoFixesMissingChannelLink(),
                affected: rows.map((row) => row.storeName),
            }))
        } else if (storesWithoutChannels.length) {
            issues.push(new IntegrationIssue({
                code: IntegrationIssueEnum.StoresWithoutChannels,
                origin: IntegrationIssueOriginEnum.Muven,
                isBlocking: false,
                title: 'Algumas empresas estão sem canal vinculado',
                detail: `Estas empresas têm o vínculo com o produto, mas nenhum canal habilitado, então ` +
                    `o produto não será enviado a canais a partir delas. ` +
                    `${this._describeWhoCreatesChannelLink()}`,
                whoFixes: this._whoFixesMissingChannelLink(),
                affected: storesWithoutChannels.map((row) => row.storeName),
            }))
        }

        const discardedChannels = allChannels.filter((channel) => channel.filteredByPriceStore)

        if (allChannels.length && discardedChannels.length === allChannels.length) {
            issues.push(new IntegrationIssue({
                code: IntegrationIssueEnum.ChannelsFilteredByPriceStore,
                origin: IntegrationIssueOriginEnum.Muven,
                isBlocking: true,
                title: 'Todos os canais foram descartados pela empresa de gestão de preço',
                detail: 'Cada um destes canais está configurado no Muven para gerenciar preço por uma ' +
                    'empresa diferente daquela em que o produto está vinculado. Por isso o Muven ignora ' +
                    'o canal neste produto. É configuração no Muven, não um problema da retaguarda.',
                whoFixes: WHO_FIXES_MUVEN_CHANNEL_CONFIG,
                affected: this._describeDiscardedChannels(discardedChannels),
            }))
        } else if (discardedChannels.length) {
            issues.push(new IntegrationIssue({
                code: IntegrationIssueEnum.ChannelsFilteredByPriceStore,
                origin: IntegrationIssueOriginEnum.Muven,
                isBlocking: false,
                title: 'Alguns canais foram descartados pela empresa de gestão de preço',
                detail: 'Estes canais gerenciam preço por outra empresa e, por isso, não aparecem ' +
                    'na empresa em que estão vinculados aqui.',
                whoFixes: WHO_FIXES_MUVEN_CHANNEL_CONFIG,
                affected: this._describeDiscardedChannels(discardedChannels),
            }))
        }

        const channelsWithoutExternalId = allChannels.filter((channel) => !channel.hasExternalChannelId)

        if (channelsWithoutExternalId.length) {
            const isTotal = channelsWithoutExternalId.length === allChannels.length
            const shouldHavePublished = channelsWithoutExternalId.filter((c) => c.exportsNewProducts === true)
            const manualOnly = channelsWithoutExternalId.filter((c) => c.exportsNewProducts !== true)

            // O canal está configurado para publicar sozinho, então o vínculo com o
            // anúncio deveria existir. Ele ter sido criado vazio indica exportação
            // que não concluiu - ou uma desassociação posterior.
            if (shouldHavePublished.length) {
                issues.push(new IntegrationIssue({
                    code: IntegrationIssueEnum.ChannelShouldHavePublished,
                    origin: IntegrationIssueOriginEnum.Muven,
                    isBlocking: isTotal && this.isEditFormBlocked(),
                    title: 'O canal deveria ter publicado este produto sozinho, mas não publicou',
                    detail: 'Estes canais estão configurados para exportar novos produtos, ou seja, o Muven ' +
                        'deveria ter criado o anúncio e gravado o identificador automaticamente. O vínculo com ' +
                        'o canal foi criado, porém sem esse identificador - o que indica exportação que não ' +
                        'concluiu, ou uma desassociação feita depois.',
                    whoFixes: WHO_FIXES_MUVEN_AUTOMATIC,
                    affected: this._describeChannelsWithExportConfig(shouldHavePublished),
                }))
            }

            if (manualOnly.length) {
                const unknownConfig = manualOnly.every((c) => c.exportsNewProducts === undefined)

                issues.push(new IntegrationIssue({
                    code: IntegrationIssueEnum.NotAssociatedInChannel,
                    origin: IntegrationIssueOriginEnum.Muven,
                    isBlocking: isTotal && !shouldHavePublished.length && this.isEditFormBlocked(),
                    title: 'O produto não está associado ao anúncio nestes canais',
                    detail: unknownConfig
                        ? 'Estes canais estão sem o identificador do anúncio. Não foi possível confirmar a ' +
                            'configuração de exportação deles, então verifique em "Canais → Configurações" se ' +
                            'o canal exporta novos produtos; caso não exporte, a associação é manual.'
                        : 'Estes canais não estão configurados para exportar novos produtos, então o Muven ' +
                            'nunca cria o anúncio sozinho: a associação precisa ser feita manualmente em ' +
                            '"Canais → Associação de produtos". Enquanto isso, preço e estoque não têm para ' +
                            'onde ser enviados.',
                    whoFixes: WHO_FIXES_MUVEN_ASSOCIATION,
                    affected: this._describeChannelsWithExportConfig(manualOnly),
                }))
            }
        }

        if (rows.every((row) => !row.storeIsActive)) {
            issues.push(new IntegrationIssue({
                code: IntegrationIssueEnum.AllStoresInactive,
                origin: IntegrationIssueOriginEnum.Retaguarda,
                isBlocking: true,
                title: 'Todas as empresas vinculadas estão inativas',
                detail: 'O produto está vinculado apenas a empresas marcadas como inativas no Muven. ' +
                    'O cadastro de empresas também vem da retaguarda.',
                whoFixes: WHO_FIXES_BACKEND,
                affected: rows.map((row) => row.storeName),
            }))
        }

        if (rows.every((row) => !row.productStoreIsActive)) {
            issues.push(new IntegrationIssue({
                code: IntegrationIssueEnum.AllProductStoresInactive,
                origin: IntegrationIssueOriginEnum.Retaguarda,
                isBlocking: true,
                title: 'Todos os vínculos com empresa estão inativos',
                detail: 'Os vínculos existem, mas chegaram da retaguarda desativados. ' +
                    'O produto é tratado como indisponível nessas empresas.',
                whoFixes: WHO_FIXES_BACKEND,
                affected: rows.map((row) => row.storeName),
            }))
        }

        const variationsWithoutStores = this._product.children.filter((child) => {
            return !(child.stores || []).length
        })

        if (variationsWithoutStores.length) {
            issues.push(new IntegrationIssue({
                code: IntegrationIssueEnum.VariationsWithoutStores,
                origin: IntegrationIssueOriginEnum.Retaguarda,
                isBlocking: !this._getParentOwnStores().length,
                title: 'Variações sem vínculo com empresa',
                detail: 'Estas variações não receberam nenhum vínculo com empresa da retaguarda, ' +
                    'portanto não têm preço, estoque nem canal.',
                whoFixes: WHO_FIXES_BACKEND,
                affected: variationsWithoutStores.map((child) => {
                    return child.getVariationsStringified() || child.skuCode || `#${child.id}`
                }),
            }))
        }

        this._issues = issues
    }

    /**
     * O Muven cria o product_store do pai sozinho depois do primeiro envio, mas
     * na Tray esse envio nunca acontece sem ele. O caminho que destrava é a
     * retaguarda exportar o vínculo do pai. Nos outros canais o pai sobe, porém
     * em alguns é criado inativo ou oculto até receber o vínculo.
     */
    private _buildParentWithoutStoresIssue(): IntegrationIssue {
        const trayChannels = this.getTrayChannelsAffected()

        if (trayChannels.length) {
            return new IntegrationIssue({
                code: IntegrationIssueEnum.ParentWithoutStores,
                origin: IntegrationIssueOriginEnum.Retaguarda,
                isBlocking: true,
                title: 'O produto pai da grade não está vinculado a nenhuma empresa',
                detail: `As variações têm vínculo com empresa, mas o produto pai não. Esta exigência é ` +
                    `específica do canal Tray: nele o produto pai é criado antes das variações e só é enviado ` +
                    `quando ele mesmo está vinculado a uma empresa ativa no canal. Sem isso, nenhuma variação ` +
                    `sobe para os canais Tray listados abaixo. Os demais canais não são bloqueados por isso. ` +
                    `O ${this._backendName} precisa exportar também o vínculo do produto pai com a empresa.`,
                whoFixes: WHO_FIXES_BACKEND,
                affected: trayChannels,
            })
        }

        return new IntegrationIssue({
            code: IntegrationIssueEnum.ParentWithoutStores,
            origin: IntegrationIssueOriginEnum.Retaguarda,
            isBlocking: false,
            title: 'O produto pai da grade não tem vínculo próprio com empresa',
            detail: 'As variações têm vínculo com empresa, mas o produto pai não. Na maioria dos canais isso ' +
                'não impede o envio, mas em alguns o produto pai é criado inativo ou oculto até que o ' +
                'vínculo seja criado, o que acontece depois do primeiro envio.',
            whoFixes: WHO_FIXES_BACKEND,
        })
    }

    /** Nomes sem repetição, para não poluir a lista em produtos com muitas variações. */
    private _describeChannelsWithExportConfig(channels: DiagnosticChannelRow[]): string[] {
        return Array.from(new Set(channels.map((channel) => {
            if (!channel.allowExportDescription) {
                return channel.name
            }

            return `${channel.name} ("${channel.allowExportDescription}")`
        })))
    }

    private _describeDiscardedChannels(channels: DiagnosticChannelRow[]): string[] {
        return Array.from(new Set(channels.map((channel) => {
            return `${channel.name} (gerencia preço pela empresa ${channel.priceManagementStoreId})`
        })))
    }
}
